/**
 * Hook for batch file uploads with progress tracking.
 *
 * Handles uploading multiple large files in sequence or parallel,
 * with aggregate progress tracking and error handling.
 */

import { useState, useCallback, useRef } from "react";

// Helper to extract error message from SDK response
function getErrorMessage(error: any, fallback: string): string {
  if (!error) return fallback;
  if (typeof error.detail === "string") return error.detail;
  if (Array.isArray(error.detail) && error.detail.length > 0) {
    return error.detail.map((e: any) => e.msg || String(e)).join(", ");
  }
  if (error.message) return error.message;
  return fallback;
}

interface BatchFile {
  id: string;
  file: File;
  status: "queued" | "uploading" | "schema_review" | "completed" | "failed";
  progress: number;
  error?: string;
  sessionId?: string;
  tableName?: string;
}

interface BatchProgress {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  currentFileIndex: number;
  currentFileName?: string;
  overallProgress: number;
  files: BatchFile[];
}

interface BatchResult {
  file: string;
  success: boolean;
  tableId?: string;
  tableName?: string;
  error?: string;
}

interface UseBatchUploadOptions {
  orgId: string;
  namespace?: string;
  /** Process files in parallel (default: false - sequential) */
  parallel?: boolean;
  /** Maximum parallel uploads (default: 2) */
  maxParallel?: number;
  /** Continue processing on file error (default: true) */
  continueOnError?: boolean;
  /** Use shared schema from first file for all files (default: false) */
  sharedSchema?: boolean;
  /** Enable schema preview for files (default: true) */
  enableSchemaPreview?: boolean;
  /** Callback when batch completes */
  onBatchComplete?: (results: BatchResult[]) => void;
  /** Callback when a single file completes */
  onFileComplete?: (file: BatchFile, result: BatchResult) => void;
  /** Callback when a single file fails */
  onFileError?: (file: BatchFile, error: Error) => void;
  /** Callback for progress updates */
  onProgress?: (progress: BatchProgress) => void;
  /** Auth headers for local-upload fallback */
  getHeaders?: () => Promise<Record<string, string>>;
  /** Base URL for local-upload fallback */
  baseUrl?: string;
}

export function useBatchUpload(options: UseBatchUploadOptions) {
  const {
    orgId,
    namespace = "default",
    parallel = false,
    maxParallel = 2,
    continueOnError = true,
    sharedSchema = false,
    enableSchemaPreview = true,
    onBatchComplete,
    onFileComplete,
    onFileError,
    onProgress,
  } = options;

  const [isProcessing, setIsProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [results, setResults] = useState<BatchResult[]>([]);

  const filesRef = useRef<BatchFile[]>([]);
  const sharedSchemaRef = useRef<any>(null);
  const abortRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Generate unique ID for files
  const generateId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Update progress and notify
  const updateProgress = useCallback(
    (updates: Partial<BatchProgress>) => {
      setBatchProgress((prev) => {
        const updated = { ...prev!, ...updates };
        onProgress?.(updated);
        return updated;
      });
    },
    [onProgress]
  );

  // Update a single file's status
  const updateFileStatus = useCallback(
    (fileId: string, updates: Partial<BatchFile>) => {
      filesRef.current = filesRef.current.map((f) =>
        f.id === fileId ? { ...f, ...updates } : f
      );

      const files = filesRef.current;
      const completedFiles = files.filter((f) => f.status === "completed").length;
      const failedFiles = files.filter((f) => f.status === "failed").length;
      const currentFile = files.find((f) => f.status === "uploading");

      updateProgress({
        files: [...files],
        completedFiles,
        failedFiles,
        currentFileName: currentFile?.file.name,
        overallProgress:
          ((completedFiles + failedFiles) / files.length) * 100 +
          (currentFile?.progress || 0) / files.length,
      });
    },
    [updateProgress]
  );

  // Process a single file
  const processFile = useCallback(
    async (batchFile: BatchFile): Promise<BatchResult> => {
      updateFileStatus(batchFile.id, { status: "uploading", progress: 0 });

      return new Promise((resolve) => {
        // We can't use the hook directly here since hooks must be called at top level
        // Instead, we'll use a simpler approach with fetch calls
        // For now, return a placeholder - the actual implementation would need
        // to either use the SDK directly or have a different architecture

        const uploadWithProgress = async () => {
          try {
            const { createUploadSession, completeUpload } = await import("../sdk");
            const { uploadToGCSResumable, uploadToLocalBackend } = await import(
              "../utils/gcs-upload"
            );

            const file = batchFile.file;

            // Create session
            const sessionResponse = await createUploadSession({
              body: {
                file_name: file.name,
                file_size: file.size,
                content_type: file.type || "application/octet-stream",
                org_id: orgId,
                namespace,
                enable_schema_preview: enableSchemaPreview && !sharedSchema,
              },
            });

            if (sessionResponse.error) {
              throw new Error(getErrorMessage(sessionResponse.error, "Session creation failed"));
            }

            const session = sessionResponse.data as any;
            updateFileStatus(batchFile.id, { sessionId: session.session_id });

            // Upload file (GCS resumable or local)
            const signal = abortControllerRef.current?.signal;
            if (session.storage_backend === "local") {
              const headers = options.getHeaders ? await options.getHeaders() : {};
              await uploadToLocalBackend(
                options.baseUrl || "",
                session.session_id,
                file,
                headers,
                (p) => updateFileStatus(batchFile.id, { progress: p.percentage }),
                signal,
              );
            } else if (session.resumable_url) {
              await uploadToGCSResumable(
                session.resumable_url,
                file,
                file.size,
                (p) => updateFileStatus(batchFile.id, { progress: p.percentage }),
                signal,
              );
            } else {
              throw new Error("No upload URL available");
            }

            if (abortRef.current) {
              throw new Error("Upload cancelled");
            }

            // Complete upload
            const completeResponse = await completeUpload({
              path: { session_id: session.session_id },
              body: { session_id: session.session_id },
            });

            if (completeResponse.error) {
              throw new Error(getErrorMessage(completeResponse.error, "Complete failed"));
            }

            const result = completeResponse.data as any;

            updateFileStatus(batchFile.id, {
              status: "completed",
              progress: 100,
              tableName: batchFile.tableName || file.name.replace(/\.[^/.]+$/, ""),
            });

            const batchResult: BatchResult = {
              file: file.name,
              success: true,
              tableId: result.file_id,
              tableName: batchFile.tableName || file.name.replace(/\.[^/.]+$/, ""),
            };

            onFileComplete?.(batchFile, batchResult);
            resolve(batchResult);
          } catch (error) {
            const err = error as Error;
            updateFileStatus(batchFile.id, {
              status: "failed",
              error: err.message,
            });

            const batchResult: BatchResult = {
              file: batchFile.file.name,
              success: false,
              error: err.message,
            };

            onFileError?.(batchFile, err);
            resolve(batchResult);
          }
        };

        uploadWithProgress();
      });
    },
    [orgId, namespace, enableSchemaPreview, sharedSchema, updateFileStatus, onFileComplete, onFileError]
  );

  // Start batch upload
  const startBatchUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      abortRef.current = false;
      abortControllerRef.current = new AbortController();
      setIsProcessing(true);
      setResults([]);

      // Initialize batch files
      const batchFiles: BatchFile[] = files.map((file) => ({
        id: generateId(),
        file,
        status: "queued" as const,
        progress: 0,
      }));

      filesRef.current = batchFiles;

      // Initialize progress
      const initialProgress: BatchProgress = {
        totalFiles: files.length,
        completedFiles: 0,
        failedFiles: 0,
        currentFileIndex: 0,
        overallProgress: 0,
        files: batchFiles,
      };

      setBatchProgress(initialProgress);
      onProgress?.(initialProgress);

      const allResults: BatchResult[] = [];

      if (parallel) {
        // Parallel processing with max concurrency
        const queue = [...batchFiles];
        const activeUploads: Promise<void>[] = [];

        const processNext = async (): Promise<void> => {
          const next = queue.shift();
          if (!next || abortRef.current) return;

          const result = await processFile(next);
          allResults.push(result);

          if (!result.success && !continueOnError) {
            abortRef.current = true;
            return;
          }

          // Process next in queue
          if (queue.length > 0) {
            await processNext();
          }
        };

        // Start initial batch
        for (let i = 0; i < Math.min(maxParallel, batchFiles.length); i++) {
          activeUploads.push(processNext());
        }

        await Promise.all(activeUploads);
      } else {
        // Sequential processing
        for (let i = 0; i < batchFiles.length; i++) {
          if (abortRef.current) break;

          const currentFile = batchFiles[i];
          if (!currentFile) continue;

          updateProgress({ currentFileIndex: i });

          const result = await processFile(currentFile);
          allResults.push(result);

          if (!result.success && !continueOnError) {
            break;
          }
        }
      }

      setResults(allResults);
      setIsProcessing(false);
      onBatchComplete?.(allResults);

      return allResults;
    },
    [parallel, maxParallel, continueOnError, processFile, updateProgress, onBatchComplete, onProgress]
  );

  // Cancel batch upload
  const cancelBatch = useCallback(() => {
    abortRef.current = true;
    abortControllerRef.current?.abort();
    setIsProcessing(false);
  }, []);

  // Add more files to the queue
  const addFiles = useCallback((newFiles: File[]) => {
    const newBatchFiles: BatchFile[] = newFiles.map((file) => ({
      id: generateId(),
      file,
      status: "queued" as const,
      progress: 0,
    }));

    filesRef.current = [...filesRef.current, ...newBatchFiles];

    if (batchProgress) {
      updateProgress({
        totalFiles: filesRef.current.length,
        files: [...filesRef.current],
      });
    }
  }, [batchProgress, updateProgress]);

  // Remove a file from the queue (only if queued)
  const removeFile = useCallback((fileId: string) => {
    const file = filesRef.current.find((f) => f.id === fileId);
    if (file?.status !== "queued") return false;

    filesRef.current = filesRef.current.filter((f) => f.id !== fileId);

    if (batchProgress) {
      updateProgress({
        totalFiles: filesRef.current.length,
        files: [...filesRef.current],
      });
    }

    return true;
  }, [batchProgress, updateProgress]);

  return {
    startBatchUpload,
    cancelBatch,
    addFiles,
    removeFile,
    isProcessing,
    batchProgress,
    results,
  };
}
